import { useState } from 'react';
import {
  Users, Copy, Share2, Gift, DollarSign, ArrowDownRight, ArrowUpRight,
  Clock, CheckCircle2, Wallet, Link2, QrCode, TrendingUp,
} from 'lucide-react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '@hooks/useAuth';
import { useReferral } from '@hooks/useReferral';
import PageHeader from '@components/layout/PageHeader';
import Button from '@components/common/Button';
import Modal from '@components/common/Modal';
import Loading from '@components/common/Loading';
import PageTutorial from '@components/common/PageTutorial';
import CurrencyInput from '@components/common/CurrencyInput';
import { formatCurrency, formatDate } from '@utils/formatters';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';

const withdrawSchema = z.object({
  amount: z.number().min(50, 'Valor minimo de R$50,00'),
  pixKey: z.string().min(1, 'Chave Pix obrigatoria'),
  pixKeyType: z.enum(['cpf', 'email', 'phone', 'random'], { required_error: 'Tipo obrigatorio' }),
});

const LEVEL_CONFIG = {
  1: { label: 'Nivel 1 (Direto)', rate: '9%', color: 'text-emerald-600 bg-emerald-50 dark:text-emerald-400 dark:bg-emerald-900/20' },
  2: { label: 'Nivel 2', rate: '5%', color: 'text-blue-600 bg-blue-50 dark:text-blue-400 dark:bg-blue-900/20' },
  3: { label: 'Nivel 3', rate: '1%', color: 'text-purple-600 bg-purple-50 dark:text-purple-400 dark:bg-purple-900/20' },
};

const Referral = () => {
  const { user } = useAuth();
  const {
    info, network, commissions, withdrawals,
    isLoadingInfo, isLoadingNetwork, isLoadingCommissions,
    requestWithdraw, isWithdrawing,
  } = useReferral();
  const [activeTab, setActiveTab] = useState('overview');
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);

  const { register, handleSubmit, reset, control, formState: { errors } } = useForm({
    resolver: zodResolver(withdrawSchema),
    defaultValues: { amount: 50, pixKey: '', pixKeyType: 'cpf' },
  });

  const frontendUrl = window.location.origin;
  const referralLink = info?.referralCode ? `${frontendUrl}/register?ref=${info.referralCode}` : '';

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado!');
  };

  const shareLink = () => {
    if (navigator.share) {
      navigator.share({
        title: 'MyBank - Controle financeiro inteligente',
        text: `Use meu codigo ${info?.referralCode} para se cadastrar no MyBank e organizar suas financas!`,
        url: referralLink,
      });
    } else {
      copyToClipboard(referralLink);
    }
  };

  const onSubmitWithdraw = async (data) => {
    await requestWithdraw(data);
    reset();
    setIsWithdrawModalOpen(false);
  };

  if (isLoadingInfo) return <Loading text="Carregando indicacoes..." />;

  const networkL1 = network.filter(n => n.level === 1);
  const networkL2 = network.filter(n => n.level === 2);
  const networkL3 = network.filter(n => n.level === 3);

  return (
    <div className="space-y-6 pb-8">
      <PageHeader
        title="Indicar Amigos"
        subtitle="Convide amigos e ganhe comissoes"
        action={
          info?.balance >= 50 && (
            <Button leftIcon={<Wallet size={18} />} onClick={() => setIsWithdrawModalOpen(true)}>
              Solicitar Saque
            </Button>
          )
        }
      />

      <PageTutorial
        pageId="referral"
        title="Programa de Indicacao"
        steps={[
          'Compartilhe seu codigo ou link com amigos e familiares',
          'Quando eles se cadastrarem e assinarem um plano, voce ganha comissao',
          'Nivel 1 (direto): 9% | Nivel 2: 5% | Nivel 3: 1% sobre a assinatura',
          'Saque suas comissoes via Pix a partir de R$50,00',
        ]}
      />

      {/* Referral Code Card */}
      <div className="bg-gradient-to-br from-primary-600 to-primary-800 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
            <Gift size={22} />
          </div>
          <div>
            <p className="font-bold text-lg">Seu codigo de indicacao</p>
            <p className="text-sm text-white/70">Compartilhe e ganhe a cada novo usuario</p>
          </div>
        </div>

        <div className="bg-white/10 backdrop-blur rounded-xl p-4 flex items-center justify-between gap-3 mb-4">
          <div className="flex-1">
            <p className="text-xs text-white/60 mb-1">Codigo</p>
            <p className="text-xl font-mono font-bold tracking-wider">{info?.referralCode || '---'}</p>
          </div>
          <Button
            size="sm"
            variant="secondary"
            leftIcon={<Copy size={14} />}
            className="bg-white/20 text-white border-white/30 hover:bg-white/30"
            onClick={() => copyToClipboard(info?.referralCode || '')}
          >
            Copiar
          </Button>
        </div>

        <Button
          variant="secondary"
          className="w-full bg-white text-primary-700 hover:bg-primary-50 border-0"
          leftIcon={<Share2 size={16} />}
          onClick={shareLink}
        >
          Compartilhar
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: 'Saldo disponivel', value: formatCurrency(info?.balance || 0), icon: Wallet, color: 'text-emerald-500' },
          { label: 'Total ganho', value: formatCurrency(info?.totalEarned || 0), icon: TrendingUp, color: 'text-blue-500' },
          { label: 'Indicados diretos', value: info?.networkL1 || 0, icon: Users, color: 'text-purple-500' },
          { label: 'Rede total', value: info?.networkCount || 0, icon: Users, color: 'text-orange-500' },
        ].map((stat) => (
          <div key={stat.label} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <stat.icon size={18} className={stat.color} />
            <p className="text-lg font-bold text-gray-900 dark:text-white mt-2">{stat.value}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Commission Structure */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-5">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Estrutura de Comissoes</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { level: 1, desc: 'Indicado direto', rate: '9%', people: info?.networkL1 || 0 },
            { level: 2, desc: 'Indicado do indicado', rate: '5%', people: info?.networkL2 || 0 },
            { level: 3, desc: 'Terceiro nivel', rate: '1%', people: info?.networkL3 || 0 },
          ].map(item => (
            <div key={item.level} className={clsx(
              'rounded-xl p-4 border',
              item.level === 1 ? 'border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/10' :
              item.level === 2 ? 'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/10' :
              'border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/10'
            )}>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{item.rate}</p>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{item.desc}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{item.people} pessoa{item.people !== 1 ? 's' : ''}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-1 w-fit">
        {[
          { id: 'overview', label: 'Rede' },
          { id: 'commissions', label: 'Comissoes' },
          { id: 'withdrawals', label: 'Saques' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={clsx(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              activeTab === tab.id
                ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Rede Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-4">
          {[
            { title: 'Nivel 1 - Indicados diretos (9%)', items: networkL1, color: 'border-l-emerald-500' },
            { title: 'Nivel 2 - Indicados indiretos (5%)', items: networkL2, color: 'border-l-blue-500' },
            { title: 'Nivel 3 - Terceiro nivel (1%)', items: networkL3, color: 'border-l-purple-500' },
          ].map(group => (
            <div key={group.title}>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{group.title}</h3>
              {group.items.length === 0 ? (
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-dashed border-gray-300 dark:border-gray-600 p-4 text-center text-sm text-gray-500">
                  Nenhum indicado neste nivel
                </div>
              ) : (
                <div className="space-y-2">
                  {group.items.map((item, idx) => (
                    <div key={idx} className={clsx('bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 border-l-4 p-4 flex items-center gap-3', group.color)}>
                      <div className="w-9 h-9 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-sm font-bold text-gray-600 dark:text-gray-300">
                        {(item.referredName || item.referredEmail || '?')[0].toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-gray-900 dark:text-white truncate">
                          {item.referredName || item.referredEmail}
                        </p>
                        <p className="text-xs text-gray-500">{formatDate(item.joinedAt)}</p>
                      </div>
                      <span className={clsx('text-xs font-medium px-2 py-0.5 rounded-full', LEVEL_CONFIG[item.level]?.color)}>
                        {LEVEL_CONFIG[item.level]?.rate}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Comissoes Tab */}
      {activeTab === 'commissions' && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          {isLoadingCommissions ? (
            <Loading text="Carregando comissoes..." />
          ) : commissions.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <DollarSign size={32} className="mx-auto mb-2 text-gray-300" />
              <p>Nenhuma comissao ainda</p>
              <p className="text-xs mt-1">Compartilhe seu codigo para comecar a ganhar!</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {commissions.map((c, idx) => (
                <div key={idx} className="flex items-center gap-3 px-5 py-3.5">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                    <ArrowDownRight size={14} className="text-emerald-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{c.fromName || 'Usuario'}</p>
                    <p className="text-xs text-gray-500">Nivel {c.level} - Plano {c.plan}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-emerald-600">{formatCurrency(c.amount)}</p>
                    <p className="text-[10px] text-gray-400">{formatDate(c.createdAt)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Saques Tab */}
      {activeTab === 'withdrawals' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Saldo: <strong className="text-gray-900 dark:text-white">{formatCurrency(info?.balance || 0)}</strong>
              {' '}(minimo para saque: R$50,00)
            </p>
            {(info?.balance || 0) >= 50 && (
              <Button size="sm" leftIcon={<Wallet size={14} />} onClick={() => setIsWithdrawModalOpen(true)}>
                Solicitar Saque
              </Button>
            )}
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            {withdrawals.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Wallet size={32} className="mx-auto mb-2 text-gray-300" />
                <p>Nenhum saque realizado</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {withdrawals.map((w, idx) => (
                  <div key={idx} className="flex items-center gap-3 px-5 py-3.5">
                    <div className={clsx('w-8 h-8 rounded-full flex items-center justify-center',
                      w.status === 'pending' ? 'bg-yellow-100 dark:bg-yellow-900/30' :
                      w.status === 'completed' ? 'bg-green-100 dark:bg-green-900/30' :
                      'bg-red-100 dark:bg-red-900/30'
                    )}>
                      {w.status === 'pending' ? <Clock size={14} className="text-yellow-600" /> :
                       w.status === 'completed' ? <CheckCircle2 size={14} className="text-green-600" /> :
                       <ArrowUpRight size={14} className="text-red-600" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">Saque via Pix</p>
                      <p className="text-xs text-gray-500">{formatDate(w.requestedAt)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-gray-900 dark:text-white">{formatCurrency(w.amount)}</p>
                      <span className={clsx('text-[10px] font-medium px-1.5 py-0.5 rounded-full',
                        w.status === 'pending' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                        w.status === 'completed' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                        'bg-red-100 text-red-700'
                      )}>
                        {w.status === 'pending' ? 'Processando' : w.status === 'completed' ? 'Pago' : 'Cancelado'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Withdraw Modal */}
      <Modal
        isOpen={isWithdrawModalOpen}
        onClose={() => { setIsWithdrawModalOpen(false); reset(); }}
        title="Solicitar Saque"
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => { setIsWithdrawModalOpen(false); reset(); }}>
              Cancelar
            </Button>
            <Button
              leftIcon={<Wallet size={16} />}
              onClick={handleSubmit(onSubmitWithdraw)}
              isLoading={isWithdrawing}
            >
              Solicitar Saque
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-3 text-center">
            <p className="text-xs text-emerald-700 dark:text-emerald-300">Saldo disponivel</p>
            <p className="text-xl font-bold text-emerald-700 dark:text-emerald-300">{formatCurrency(info?.balance || 0)}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Valor do saque</label>
            <Controller
              name="amount"
              control={control}
              render={({ field }) => (
                <CurrencyInput
                  value={field.value}
                  onValueChange={(values) => field.onChange(values.floatValue ?? 0)}
                  onBlur={field.onBlur}
                  placeholder="R$ 50,00"
                />
              )}
            />
            {errors.amount && <p className="text-xs text-red-500 mt-1">{errors.amount.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tipo de chave Pix</label>
            <select
              className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              {...register('pixKeyType')}
            >
              <option value="cpf">CPF</option>
              <option value="email">E-mail</option>
              <option value="phone">Telefone</option>
              <option value="random">Chave aleatoria</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Chave Pix</label>
            <input
              type="text"
              placeholder="Digite sua chave Pix"
              className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
              {...register('pixKey')}
            />
            {errors.pixKey && <p className="text-xs text-red-500 mt-1">{errors.pixKey.message}</p>}
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-3">
            <p className="text-xs text-blue-700 dark:text-blue-300">
              O saque sera processado em ate 3 dias uteis. Valor minimo: R$50,00.
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Referral;
